import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { ArrowLeft, Shield, Lock, Eye, Clock, UserCheck, Database } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PrivacyStatement() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";

  const accent = isCyber ? "text-cyan-400" : "text-divine-gold";
  const accentBorder = isCyber ? "border-cyan-500/30" : "border-divine-gold/30";
  const accentBg = isCyber ? "bg-cyan-500/10" : "bg-divine-gold/10";
  const headingFont = isCyber ? "font-bold tracking-tight" : "font-cinzel";

  return (
    <div className="min-h-screen bg-divine-black text-white" data-testid="privacy-statement-page">
      {/* Background */}
      {isCyber ? (
        <div className="fixed inset-0 cyber-grid-bg opacity-10 pointer-events-none" />
      ) : (
        <div className="fixed inset-0 bg-gradient-to-b from-divine-black via-[#0a0a1a] to-divine-black pointer-events-none" />
      )}

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className={`mb-8 ${accent} hover:bg-white/5`}
          data-testid="privacy-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${accentBg} border ${accentBorder} mb-6`}>
            <Shield className={`w-8 h-8 ${accent}`} />
          </div>
          <h1 className={`text-3xl sm:text-4xl ${headingFont} mb-3`} data-testid="privacy-title">
            Privacy Statement
          </h1>
          <p className="text-white/50 text-sm">
            AgentEros.AI &mdash; Effective April 2026 &mdash; Version 1.0
          </p>
        </div>

        {/* Opening statement */}
        <div className={`rounded-lg border ${accentBorder} ${accentBg} p-6 mb-10`}>
          <p className="text-white/90 text-lg leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            We take your privacy very seriously. We understand the sensitive nature of the information
            Agent Eros requires to provide a world-class service. <strong className={accent}>Your data belongs to you and only you.</strong>
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          <Section
            icon={Lock}
            title="Your Data Is Never Shared or Sold"
            accent={accent}
            accentBorder={accentBorder}
            headingFont={headingFont}
          >
            Under no circumstances is your data shared, sold, or used for any purpose other than
            providing you the best relationship advice possible with the latest advancements in AI
            data analysis. We do not sell your information to third parties. We do not use your data
            for advertising. Your conversations, profiles, and personal details exist solely to
            power your Agent Eros experience.
          </Section>

          <Section
            icon={UserCheck}
            title="Aliases & Data Siloing"
            accent={accent}
            accentBorder={accentBorder}
            headingFont={headingFont}
          >
            You can ensure additional data protection by using aliases throughout the platform.
            All your data remains siloed within the Agent Eros platform with no connection to
            outside information resources. This means your identity and personal details are
            compartmentalised and never cross-referenced with external databases, social media,
            or third-party services.
          </Section>

          <Section
            icon={Clock}
            title="AI Session Data Retention"
            accent={accent}
            accentBorder={accentBorder}
            headingFont={headingFont}
          >
            No session data is retained in any of the AI LLM systems beyond the immediate active
            session window of activity. All AI processing context is automatically cleared after
            <strong className={accent}> 5 minutes of inactivity</strong>. This ensures that your
            conversations are not stored, cached, or accessible within any external AI infrastructure
            once your session concludes.
          </Section>

          <Section
            icon={Database}
            title="Data Storage & Security"
            accent={accent}
            accentBorder={accentBorder}
            headingFont={headingFont}
          >
            Your personal data is stored securely within our encrypted infrastructure. We employ
            industry-standard security measures to protect against unauthorised access, alteration,
            disclosure, or destruction of your information. Access to user data is strictly limited
            to essential platform operations.
          </Section>

          <Section
            icon={Eye}
            title="Your Rights"
            accent={accent}
            accentBorder={accentBorder}
            headingFont={headingFont}
          >
            You retain full ownership of your data at all times. You may request a copy of your
            data, request corrections, or request complete deletion of your account and all
            associated data by contacting us at{" "}
            <a href="mailto:support@agenteros.ai" className={`${accent} underline underline-offset-2`}>
              support@agenteros.ai
            </a>. Upon receiving a verified deletion request, all personal data will be permanently
            removed from our systems within 30 days.
          </Section>
        </div>

        {/* Governing Law */}
        <div className={`mt-12 pt-8 border-t ${accentBorder}`}>
          <h3 className={`text-lg ${headingFont} mb-3`}>Governing Law</h3>
          <p className="text-white/60 text-sm leading-relaxed">
            This Privacy Statement is governed by the laws of the State of Victoria, Australia, and
            is subject to the provisions of the Australian Privacy Act 1988 (Cth) and the Australian
            Privacy Principles (APPs). For any privacy-related inquiries or complaints, contact us at{" "}
            <a href="mailto:support@agenteros.ai" className={`${accent} underline underline-offset-2`}>
              support@agenteros.ai
            </a>.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-white/30 text-xs">
          <p>&copy; {new Date().getFullYear()} AgentEros.AI &mdash; ABN 029 512 917 800</p>
          <p className="mt-1">All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, accent, accentBorder, headingFont, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-5 h-5 ${accent} flex-shrink-0`} />
        <h2 className={`text-xl ${headingFont}`}>{title}</h2>
      </div>
      <p className={`text-white/70 leading-relaxed pl-8 border-l ${accentBorder}`}>
        {children}
      </p>
    </div>
  );
}
